import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Phone } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { useSession } from '@/api/session';
import { useDossier } from '@/api/hooks/dossier';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, Stat } from '@/components/ui/Card';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { PhoneCodeButton } from '@/components/dispatch/PhoneCodeDialog';
import { IncidentsCard } from '@/components/drivers/IncidentsCard';
import { PersonalDocumentsCard } from '@/components/drivers/PersonalDocumentsCard';

const STATUS_BG: Record<string, string> = {
  active: 'На работа',
  on_leave: 'В отпуск',
  inactive: 'Неактивен',
};

/**
 * ─────────────────────────────────────────────────────────────────────────
 *  ШОФЬОРЪТ — ДОСИЕ
 *
 *  Клик върху шофьор в „Шофьори" води тук: сроковете (книжка, медицинско…),
 *  колко е карал, произшествията и щетите — вписани и записани от системата —
 *  и личните документи (само за управителя).
 * ─────────────────────────────────────────────────────────────────────────
 */
export function DriverPage() {
  const { driverId } = useParams({ from: '/drivers/$driverId' });
  const role = useSession((st) => st.user?.role);
  const dossier = useDossier(driverId);

  if (dossier.isLoading) return <LoadingBlock />;
  if (dossier.isError || !dossier.data) return <ErrorState onRetry={() => void dossier.refetch()} />;
  const { driver, expiries, stats, incidents, events } = dossier.data;
  const canEdit = role === 'owner' || role === 'dispatcher';

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/drivers" className="text-ink-dim hover:text-accent" title="Към шофьорите">
          <ArrowLeft className="size-5" />
        </Link>
        <div>
          <h1 className="font-display text-lg font-bold tracking-tight">{driver.fullName}</h1>
          <div className="text-xs text-ink-dim">
            {[
              driver.homeBase,
              driver.hiredOn ? `при нас от ${formatDate(driver.hiredOn)}` : null,
              driver.licenceCategories.length ? `кат. ${driver.licenceCategories.join(', ')}` : null,
              driver.languages.length ? `езици: ${driver.languages.join(', ')}` : null,
            ]
              .filter(Boolean)
              .join(' · ') || '—'}
          </div>
        </div>
        <Badge tone={driver.status === 'active' ? 'ok' : 'warn'}>{STATUS_BG[driver.status] ?? driver.status}</Badge>
        {!driver.hasPhoneApp ? <Badge tone="neutral">без приложение</Badge> : null}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {driver.phone ? (
            <a
              href={`tel:${driver.phone}`}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-accent/40 bg-accent-soft px-3 font-mono text-sm text-accent"
            >
              <Phone className="size-3.5" />
              {driver.phone}
            </a>
          ) : null}
          <PhoneCodeButton driverId={driver.id} driverName={driver.fullName} />
          <Link to="/drivers" search={{ edit: driver.id }}>
            <Button size="sm" variant="secondary" icon={<Pencil className="size-3.5" />}>
              Данни на шофьора
            </Button>
          </Link>
        </div>
      </div>

      <Card>
        <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Stat label="Курсове за 12 месеца" value={stats.tripsLastYear.toLocaleString('bg-BG')} hint={`общо ${stats.tripsTotal}`} />
          <Stat
            label="Километри за 12 месеца"
            value={`${stats.kmLastYear.toLocaleString('bg-BG')} км`}
            hint={stats.lastTripAt ? `последен курс ${formatDate(stats.lastTripAt)}` : undefined}
          />
          <Stat label="В момента" value={stats.tripsActive ? `${stats.tripsActive} курс${stats.tripsActive === 1 ? '' : 'а'}` : 'свободен'} />
          <Stat
            label="ПТП за 3 години"
            value={stats.accidents3y}
            hint={stats.accidents3y ? `по негова вина: ${stats.accidentsAtFault3y}` : undefined}
            tone={stats.accidentsAtFault3y ? 'danger' : stats.accidents3y ? 'warn' : 'default'}
          />
          <Stat
            label="Щети и глоби за 3 години"
            value={stats.damageEur3y ? `${stats.damageEur3y.toLocaleString('bg-BG', { maximumFractionDigits: 2 })} €` : '—'}
          />
          <div className="col-span-2 flex flex-wrap gap-1.5 border-t border-line pt-3 text-xs sm:col-span-5">
            {expiries.map((e) => (
              <Badge
                key={e.key}
                tone={e.daysLeft === null ? 'neutral' : e.daysLeft < 0 ? 'danger' : e.daysLeft <= 45 ? 'warn' : 'ok'}
              >
                {e.label}: {e.date ? `${e.daysLeft !== null && e.daysLeft < 0 ? 'изтекло ' : 'до '}${formatDate(e.date)}` : 'не е въведено'}
              </Badge>
            ))}
          </div>
        </CardBody>
      </Card>

      <IncidentsCard driverId={driver.id} incidents={incidents} events={events} canEdit={canEdit} />

      {role === 'owner' ? <PersonalDocumentsCard driverId={driver.id} /> : null}

      {driver.notes ? (
        <Card>
          <CardBody className="text-sm">
            <span className="text-xs font-medium uppercase tracking-wide text-ink-faint">Бележки · </span>
            {driver.notes}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
